import { useEffect, useState } from "react";
import { ExternalLink, Newspaper, Calendar, Tag, AlertTriangle, Target, ArrowRight } from "lucide-react";
import SectionFadeIn from "@/components/SectionFadeIn";
import { Link } from "react-router-dom";

interface NewsItem {
  id: string;
  title: string;
  summary: string;
  impact?: string[];
  action?: string;
  date: string;
  source: string;
  url?: string;
  tag: "deadline" | "rule-change" | "enforcement" | "announcement";
}

const TAG_STYLES: Record<NewsItem["tag"], string> = {
  deadline: "bg-red-500/15 text-red-400 border-red-500/30",
  "rule-change": "bg-blue-500/15 text-blue-400 border-blue-500/30",
  enforcement: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  announcement: "bg-brand-yellow/15 text-brand-yellow border-brand-yellow/30",
};

const TAG_LABELS: Record<NewsItem["tag"], string> = {
  deadline: "Deadline",
  "rule-change": "Rule Change",
  enforcement: "Enforcement",
  announcement: "Announcement",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface Props {
  compact?: boolean;
}

export default function LL11NewsSection({ compact = false }: Props) {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/ll11-news")
      .then((r) => r.json())
      .then((data: NewsItem[]) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const displayed = compact ? items.slice(0, 3) : items;

  if (loading) {
    return (
      <section className={compact ? "" : "section-padding bg-secondary"}>
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="flex gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex-1 h-32 bg-border/40 rounded-sm animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (displayed.length === 0) return null;

  if (compact) {
    return (
      <section className="bg-[#0a0a0a] border-t border-white/10 py-10">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <SectionFadeIn>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Newspaper className="h-4 w-4 text-brand-yellow" />
                <span className="text-xs font-bold uppercase tracking-widest text-brand-yellow">
                  LL11 / FISP Updates
                </span>
              </div>
              <Link
                to="/local-law-11"
                className="text-xs text-white/40 hover:text-brand-yellow transition-colors uppercase tracking-wider"
              >
                All updates →
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {displayed.map((item) => (
                <CompactCard key={item.id} item={item} />
              ))}
            </div>
          </SectionFadeIn>
        </div>
      </section>
    );
  }

  return (
    <section className="section-padding bg-secondary">
      <div className="container-max px-4 sm:px-6 lg:px-8">
        <SectionFadeIn>
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="h-1 w-12 bg-brand-yellow mb-4" />
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-2">
                LL11 / FISP News & Updates
              </h2>
              <p className="text-muted-foreground text-sm max-w-xl">
                Latest DOB announcements, rule changes, deadlines, and enforcement
                actions affecting NYC building owners — with clear impact and next steps.
              </p>
            </div>
          </div>
        </SectionFadeIn>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {displayed.map((item, i) => (
            <SectionFadeIn key={item.id} delay={i * 0.05}>
              <FullCard item={item} />
            </SectionFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function CompactCard({ item }: { item: NewsItem }) {
  const Wrapper = item.url ? "a" : "div";
  const wrapperProps = item.url
    ? { href: item.url, target: "_blank", rel: "noopener noreferrer" }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      className="group block bg-white/5 border border-white/10 rounded-sm p-4 hover:border-brand-yellow/40 transition-colors cursor-default"
    >
      <div className="flex items-center justify-between mb-2">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border ${TAG_STYLES[item.tag]}`}
        >
          <Tag className="h-2.5 w-2.5" />
          {TAG_LABELS[item.tag]}
        </span>
        {item.url && (
          <ExternalLink className="h-3 w-3 text-white/20 group-hover:text-brand-yellow transition-colors" />
        )}
      </div>
      <p className="text-white text-sm font-semibold leading-snug mb-2 line-clamp-2">
        {item.title}
      </p>
      <div className="flex items-center gap-1.5 text-[10px] text-white/40">
        <Calendar className="h-2.5 w-2.5" />
        {formatDate(item.date)}
        <span className="ml-1">· {item.source}</span>
      </div>
    </Wrapper>
  );
}

function FullCard({ item }: { item: NewsItem }) {
  return (
    <article className="group bg-background border border-border rounded-sm p-6 sm:p-7 hover:border-brand-yellow/50 hover:shadow-md transition-all h-full flex flex-col">
      {/* Header: tag + date */}
      <div className="flex items-center justify-between gap-2 mb-4">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border ${TAG_STYLES[item.tag]}`}
        >
          <Tag className="h-2.5 w-2.5" />
          {TAG_LABELS[item.tag]}
        </span>
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Calendar className="h-3 w-3" />
          {formatDate(item.date)}
          <span className="ml-1 font-medium">· {item.source}</span>
        </div>
      </div>

      {/* Title */}
      <h3 className="text-foreground font-bold text-lg leading-tight mb-3">
        {item.title}
      </h3>

      {/* Summary / Context */}
      <p className="text-muted-foreground text-sm leading-relaxed mb-5">
        {item.summary}
      </p>

      {/* What Changes / Impact */}
      {item.impact && item.impact.length > 0 && (
        <div className="bg-secondary/60 border-l-2 border-brand-yellow pl-4 pr-4 py-3 mb-5 rounded-r-sm">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle className="h-3.5 w-3.5 text-brand-yellow" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-brand-yellow">
              What This Changes
            </span>
          </div>
          <ul className="space-y-1.5">
            {item.impact.map((point, i) => (
              <li key={i} className="text-foreground text-xs leading-relaxed flex gap-2">
                <span className="text-brand-yellow flex-shrink-0 mt-1">•</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action */}
      {item.action && (
        <div className="flex items-start gap-2 mb-5 pb-5 border-b border-border">
          <Target className="h-4 w-4 text-foreground flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
              Recommended Action
            </div>
            <p className="text-foreground text-xs leading-relaxed">{item.action}</p>
          </div>
        </div>
      )}

      {/* Footer links */}
      <div className="mt-auto flex flex-wrap items-center gap-3 text-xs">
        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-brand-yellow transition-colors"
          >
            Official source
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
        <Link
          to="/contact"
          className="inline-flex items-center gap-1 text-brand-yellow hover:underline font-semibold ml-auto"
        >
          Talk to our team
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </article>
  );
}
